import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { ChartConfiguration, ChartData, ChartType } from 'chart.js';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { BaseChartDirective } from 'ng2-charts';
import * as Orders from '../../domain';
import { getComcastChartColorByIndex } from 'src/app/shared/constants/chart-colors';

@Component({
  selector: 'app-active-order-chart',
  templateUrl: './active-order-chart.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [MatProgressSpinnerModule, BaseChartDirective],
})
export class ActiveOrderChartComponent {
  readonly data = input<{ [key: string]: number }>({});

  readonly order = input<any>();

  readonly loading = input<boolean>(true);

  private readonly chart = viewChild(BaseChartDirective);

  private readonly statusOrder = ['In Progress', 'Pending', 'Activation Complete'];

  private readonly statusColors: Record<string, string> = {
    'In Progress': getComcastChartColorByIndex(0),
    Pending: getComcastChartColorByIndex(1),
    'Activation Complete': getComcastChartColorByIndex(2),
  };

  private readonly ordersTableLink = '/orders-table';

  pieChartData: ChartData<'doughnut'> = {
    labels: [],
    datasets: [
      {
        data: [],
        label: 'Active Orders',
        borderWidth: 0,
        hoverBorderWidth: 0,
        backgroundColor: this.statusOrder.map(
          (status) => this.statusColors[status],
        ),
      },
    ],
  };

  readonly pieChartType: ChartType = 'doughnut';

  readonly pieChartOptions: ChartConfiguration<'doughnut'>['options'] = {
    maintainAspectRatio: false,
    responsive: true,
    cutout: '62%',
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(17, 24, 39, 0.96)',
        titleColor: '#FFFFFF',
        bodyColor: '#FFFFFF',
        titleFont: {
          family: 'Montserrat',
          size: 14,
          weight: 'bold',
        },
        bodyFont: {
          family: 'Lato',
          size: 13,
          weight: 'normal',
        },
        padding: { top: 12, right: 14, bottom: 12, left: 14 },
        caretPadding: 8,
        caretSize: 8,
        cornerRadius: 8,
        boxPadding: 6,
        bodySpacing: 6,
        titleMarginBottom: 8,
        displayColors: true,
        usePointStyle: true,
        callbacks: {
          label: (context) => `Orders: ${context.parsed}`,
          labelPointStyle: () => ({ pointStyle: 'rectRounded', rotation: 0 }),
        },
      },
    },
    layout: { padding: 4 },
    onHover: (event, chartElement) => {
      const target = event.native?.target as HTMLElement;
      if (target && target.style) {
        target.style.cursor = chartElement.length ? 'pointer' : 'default';
      }
    },
  };

  readonly legendItems = signal<
    { label: string; color: string; count: number }[]
  >([]);

  readonly chartAriaLabel = computed(() => {
    const items = this.legendItems();
    if (!items.length) return 'No order status data available';
    return `Order status breakdown: ${items.map((item) => `${item.label} ${item.count}`).join(', ')}.`;
  });

  constructor(private router: Router) {
    effect(() => {
      const data = this.data();
      if (data && Object.keys(data).length > 0) {
        untracked(() => this.setData(data));
      } else {
        untracked(() => this.clearChart());
      }
    });
  }

  chartClicked(e: any): void {
    if (!e.active?.length) return;
    const label = this.pieChartData.labels?.[e.active[0].index] as string;
    if (!label) return;
    const url = this.getLabelUrl(label);
    this.router.navigateByUrl(url);
  }

  private getLabelUrl(label: string): string {
    let statuses;
    if (label.includes('In Progress')) {
      statuses = [Orders.Status.IN_PROGRESS];
    }
    if (label.includes('Pending') || label.includes('Ready')) {
      statuses = [Orders.Status.PENDING];
    }
    return `${this.ordersTableLink}?status=${statuses?.join(',') ?? ''}`;
  }

  private clearChart(): void {
    this.pieChartData.labels = [];
    this.pieChartData.datasets[0].data = [];
    this.chart()?.update();
    this.legendItems.set([]);
  }

  private setData(data: { [key: string]: number }): void {
    const dataByStatus: Record<string, number> = {};

    Object.entries(data).forEach(([key, value]) => {
      if (key === 'Ready' || key === 'Pending') {
        dataByStatus['Pending'] = (dataByStatus['Pending'] ?? 0) + value;
      } else {
        const label = key.charAt(0).toUpperCase() + key.slice(1);
        dataByStatus[label] = (dataByStatus[label] ?? 0) + value;
      }
    });

    const chartData = this.statusOrder.map(
      (status) => dataByStatus[status] ?? 0,
    );
    const isEmpty = !chartData.some((v) => v > 0);

    this.pieChartData.labels = isEmpty ? [] : [...this.statusOrder];
    this.pieChartData.datasets[0].data = isEmpty ? [] : chartData;
    this.pieChartData.datasets[0].backgroundColor = this.statusOrder.map(
      (s) => this.statusColors[s],
    );
    this.chart()?.update();

    const total = chartData.reduce((s, v) => s + v, 0);
    this.legendItems.set(
      total === 0
        ? []
        : this.statusOrder
            .map((status, i) => ({
              label: status,
              color: this.statusColors[status],
              count: chartData[i],
            }))
            .filter((item) => item.count > 0),
    );
  }
}
